import clsx from "clsx";
import { Form, Formik, type FormikProps } from "formik";
import type {
  GetAssetListAssetGetQueryParams,
  GetAssetPositionListAssetPositionGetQueryParams,
  GetAssetRegisterListAssetRegisterGetQueryParams,
} from "kubb";
import { isEqual, isUndefined } from "lodash-es";
import type React from "react";
import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AssetUseCaseSelect from "../../../../components/Asset/AssetUseCaseSelect";
import AssetPositionGroupSelectCombo from "../../../../components/AssetPositionGroup/AssetPositionGroupSelectCombo.tsx";
import { ButtonGroupField } from "../../../../components/Forms/ButtonGroup";
import NewTextFormComponent from "../../../../components/Forms/NewTextFormComponent";
import SchemeSelectFormComponent from "../../../../components/Scheme/SchemeSelectFormComponent.tsx";
import { Button } from "../../../../components/Theme/button";
import { Col, Container } from "../../../../components/Theme/grid";
import {
  convertStringUndefined,
  createSearchString,
} from "../../../../components/navigation/SearchString";
import AssetTypeSelect from "../AssetTypeSelect";

export type AssetListFilterParams = Omit<
  | GetAssetListAssetGetQueryParams
  | GetAssetPositionListAssetPositionGetQueryParams
  | GetAssetRegisterListAssetRegisterGetQueryParams,
  "company_id"
>;

type Props = {
  params: AssetListFilterParams;
  includeNonCreateables?: boolean;
  filterUnassignedField?: string;
  includeTextSearch?: boolean;
  includeGroup?: boolean;
  includeUseCase?: boolean;
  includeStatus?: boolean;
  includeIssueStatus?: boolean;
  includeSchemeFilter?: boolean;
  includeRetiredFilter?: boolean;
  onSubmit?: (values: AssetListFilterParams) => void;
  dense?: boolean;
};

function isAssetPositionParams(
  params:
    | GetAssetPositionListAssetPositionGetQueryParams
    | GetAssetListAssetGetQueryParams,
): params is GetAssetPositionListAssetPositionGetQueryParams {
  return (
    (params as GetAssetPositionListAssetPositionGetQueryParams).status !==
    undefined
  );
}

function isAssetRegisterParams(
  params:
    | GetAssetPositionListAssetPositionGetQueryParams
    | GetAssetListAssetGetQueryParams
    | GetAssetRegisterListAssetRegisterGetQueryParams,
): params is GetAssetRegisterListAssetRegisterGetQueryParams {
  return (
    (params as GetAssetRegisterListAssetRegisterGetQueryParams).issue_status !==
    undefined
  );
}

export default function AssetListFilterForm(props: Props): React.ReactElement {
  const navigate = useNavigate();
  const formikRef = useRef<FormikProps<Record<string, unknown>>>(null);

  const {
    filterUnassignedField = "asset_position_id",
    includeNonCreateables = true,
    includeTextSearch = false,
    includeGroup = true,
    includeUseCase = true,
    includeStatus = true,
    includeIssueStatus = true,
    includeSchemeFilter = false,
    includeRetiredFilter = true,
  } = props;

  const setFiltersToRoute = useCallback(
    (values: AssetListFilterParams): void => {
      if (!props.includeSchemeFilter) {
        values.scheme_id = undefined;
      }
      const newSearchString = createSearchString(
        convertStringUndefined({ ...values }),
      );

      if (
        location.search === ""
          ? newSearchString !== ""
          : `?${newSearchString}` !== location.search
      ) {
        navigate({
          ...location,
          search: newSearchString,
        });
      }
    },
    [navigate, props.includeSchemeFilter],
  );

  const currentValues = {
    ...props.params,
    asset_position_id: isUndefined(props.params.asset_position_id)
      ? "undefined"
      : props.params.asset_position_id,

    asset_id: isUndefined(props.params.asset_id)
      ? "undefined"
      : props.params.asset_id,
    status:
      isAssetPositionParams(props.params) &&
      props.params.status &&
      props.params.status.length >= 1
        ? props.params.status
        : [],
    issue_status:
      isAssetRegisterParams(props.params) && props.params.issue_status
        ? props.params.issue_status
        : ["OK", "WARNING", "ERROR"],
  } as AssetListFilterParams;

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={currentValues}
        onSubmit={(x: AssetListFilterParams): void => {
          if (props.onSubmit) {
            props.onSubmit(x);
          } else {
            setFiltersToRoute(x);
          }
        }}
      >
        {({
          values,
        }: {
          values: AssetListFilterParams;
        }): React.ReactElement => (
          <Form>
            <Container gap={props.dense ? 1 : 3} className={"grid-"}>
              {includeSchemeFilter && (
                <Col sm={12}>
                  <SchemeSelectFormComponent
                    allowAll={true}
                    fieldName={"scheme_id"}
                    label={{ label: "Scheme", size: "sm" }}
                  />
                </Col>
              )}
              {includeTextSearch && (
                <Col sm={12} md={6}>
                  <NewTextFormComponent
                    label={{ label: "Search", size: "sm" }}
                    fieldName={"text_search"}
                  />
                </Col>
              )}
              <Col sm={12} md={6}>
                <AssetTypeSelect
                  fieldName={"asset_type"}
                  label={{ label: "Asset Type", size: "sm" }}
                  allowUnknown={true}
                  includeNonCreateables={includeNonCreateables}
                  isMulti={true}
                />
              </Col>
              {includeUseCase && (
                <Col sm={12} md={includeGroup && includeTextSearch ? 3 : 6}>
                  <AssetUseCaseSelect
                    fieldName={"use_case"}
                    label={{ label: "Use Case", size: "sm" }}
                    includeAll={false}
                    isMulti={true}
                    includeNone={true}
                  />
                </Col>
              )}
              {includeGroup && (
                <Col sm={12} md={includeUseCase && includeTextSearch ? 3 : 6}>
                  <AssetPositionGroupSelectCombo
                    fieldName={"group"}
                    label={{ label: "Group", size: "sm" }}
                    useGivenSchemeId={includeSchemeFilter}
                    schemeId={
                      includeSchemeFilter ? values.scheme_id : undefined
                    }
                  />
                </Col>
              )}
              <Col
                sm={12}
                md={includeStatus || includeRetiredFilter ? 2 : 4}
                className={clsx(
                  !includeUseCase && !includeGroup && "md:col-start-7",
                )}
              >
                <ButtonGroupField
                  fieldName={filterUnassignedField}
                  label={{ label: "Filter Unassigned", size: "sm" }}
                  options={[
                    { label: "Yes", value: "NONE" },
                    {
                      label: "No",
                      value: "undefined",
                    },
                  ]}
                  fullWidth={true}
                />
              </Col>
              {includeStatus && (
                <Col sm={12} md={2}>
                  <ButtonGroupField
                    fieldName={"status"}
                    label={{ label: "Status", size: "sm" }}
                    options={[
                      { label: "Active", value: "ACTIVE" },
                      {
                        label: "Deleted",
                        value: "DELETED",
                      },
                    ]}
                    fullWidth={true}
                  />
                </Col>
              )}
              {includeRetiredFilter && (
                <Col sm={12} md={2}>
                  <ButtonGroupField
                    fieldName={"exclude_retired"}
                    label={{ label: "Retired Assets", size: "sm" }}
                    options={[
                      { label: "Hide", value: true },
                      { label: "Show", value: false },
                    ]}
                    fullWidth={true}
                  />
                </Col>
              )}
              {includeIssueStatus && (
                <Col sm={12} md={2}>
                  <ButtonGroupField
                    fieldName={"issue_status"}
                    label={{ label: "Issues", size: "sm" }}
                    options={[
                      { label: "All", value: ["OK", "WARNING", "ERROR"] },
                      { label: "Ok", value: ["OK"] },
                      {
                        label: "Error",
                        value: ["ERROR"],
                      },
                    ]}
                    fullWidth={true}
                  />
                </Col>
              )}
              {includeIssueStatus && (
                <Col sm={0} md={10}>
                  &nbsp;
                </Col>
              )}
              <Col sm={12} md={2} className={"flex-justify-end mt-auto"}>
                <Button
                  type={"submit"}
                  className={"w-full md:h-[38px]"}
                  disabled={isEqual(values, currentValues)}
                >
                  Filter
                </Button>
              </Col>
            </Container>
          </Form>
        )}
      </Formik>
    </>
  );
}
